import { useCallback, useEffect, useMemo, useState } from 'react';
import { flattenGroups, type OptionProp, type OptionsGroup } from '../../shared/normalize-options';

type TypeaheadMatchFn = (args: { option: OptionProp; query: string }) => boolean;

type UseTypeaheadFilteringParams = {
    groups: OptionsGroup;
    matchOption?: TypeaheadMatchFn;
};

type UseTypeaheadFilteringResult = {
    inputValue: string;
    visibleGroups: OptionsGroup;
    flattenedVisibleOptions: OptionProp[];
    isOpen: boolean;
    setInputValue: (value: string) => void;
    open: () => void;
    close: () => void;
    reset: () => void;
};

const defaultMatch: TypeaheadMatchFn = ({ option, query }) => {
    const lowered = query.toLowerCase();
    return option.label.toLowerCase().includes(lowered) || option.value.toLowerCase().includes(lowered);
};

const equalsOptionsGroup = (a: OptionsGroup, b: OptionsGroup) => {
    if (a.length !== b.length) return false;
    return a.every((group, index) => {
        const other = b[index];
        if (group.label !== other.label || group.key !== other.key) return false;
        if (group.options.length !== other.options.length) return false;
        return group.options.every((option, optionIndex) => option.value === other.options[optionIndex].value);
    });
};

const useTypeaheadFiltering = ({ groups, matchOption = defaultMatch }: UseTypeaheadFilteringParams): UseTypeaheadFilteringResult => {
    const [inputValue, setInputValueState] = useState('');
    const [visibleGroups, setVisibleGroups] = useState<OptionsGroup>(groups);
    const [isOpen, setIsOpen] = useState(false);

    const filterGroups = useCallback(
        (query: string) => {
            if (!query) return groups;
            const filtered = groups.reduce<OptionsGroup>((acc, group) => {
                const options = group.options.filter((option) => matchOption({ option, query }));
                if (options.length > 0) {
                    acc.push({ ...group, options });
                }
                return acc;
            }, []);
            return filtered;
        },
        [groups, matchOption]
    );

    const setInputValue = useCallback(
        (value: string) => {
            setInputValueState(value);
            setVisibleGroups(filterGroups(value));
        },
        [filterGroups]
    );

    const open = useCallback(() => {
        setIsOpen(true);
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
        setVisibleGroups((current) => {
            if (equalsOptionsGroup(current, groups)) return current;
            return groups;
        });
    }, [groups]);

    const reset = useCallback(() => {
        setInputValueState('');
        setVisibleGroups(groups);
        setIsOpen(false);
    }, [groups]);

    useEffect(() => {
        setVisibleGroups((previous) => {
            const next = filterGroups(inputValue);
            if (equalsOptionsGroup(previous, next)) return previous;
            return next;
        });
    }, [filterGroups, inputValue]);

    useEffect(() => {
        setVisibleGroups((previous) => {
            if (equalsOptionsGroup(previous, groups)) return previous;
            return groups;
        });
    }, [groups]);

    const flattenedVisibleOptions = useMemo(() => flattenGroups(visibleGroups), [visibleGroups]);

    return {
        inputValue,
        visibleGroups,
        flattenedVisibleOptions,
        isOpen,
        setInputValue,
        open,
        close,
        reset,
    };
};

export type { TypeaheadMatchFn, UseTypeaheadFilteringParams, UseTypeaheadFilteringResult };
export { useTypeaheadFiltering };
